import * as React from 'react';
import { useToast } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsCompareAdded } from '../../selectors/compare';
import { getGALabel } from '../../selectors/newbuilding/getGALabel';
import { getNewbuildingId } from '../../selectors/newbuilding';
import { getIsAuthenticated } from '../../selectors/user';
import { requestAuthentication } from '../../actions/authentication';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { toggleCompare } from '../../slices/compare';
import { TDispatch } from '../../types/redux';
import { useApplicationContext } from '../../utils/applicationContext';
import { CompareButtonControl } from './CompareButtonControl';
import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import { trackAddNewbuildingToComparison, trackRemoveNewbuildingFromComparison } from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingCompareButtonProps {
  hideText?: boolean;
  isHeader?: boolean;
  withOnboarding?: boolean;
}

export const NewbuildingCompareButton: React.FC<INewbuildingCompareButtonProps> = ({
  hideText = false,
  isHeader = false,
  withOnboarding = false,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch<TDispatch>();
  const isAdded = useSelector(selectIsCompareAdded);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const newbuildingId = useSelector(getNewbuildingId);
  const gaLabel = useSelector(getGALabel);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const { open, closeHandler } = useCompareOnboarding(withOnboarding && isCompareEnabled);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        content: <CompareToastContent text={result.text} comparisonUrl={result.comparisonUrl} />,
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    const nextValue = !isAdded;

    if (!isAuthenticated) {
      dispatch(requestAuthentication({}));

      return;
    }

    dispatch(toggleCompare());

    try {
      const result = nextValue
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      showComparisonToast(result);

      if (result.success === false) {
        dispatch(toggleCompare());

        return;
      }

      if (nextValue) {
        trackAddNewbuildingToComparison(newbuildingId, gaLabel);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId, gaLabel);
      }
    } catch (_error) {
      dispatch(toggleCompare());
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [dispatch, gaLabel, httpApi, isAdded, isAuthenticated, logger, newbuildingId, showComparisonToast]);

  if (!isCompareEnabled) {
    return null;
  }

  const button = (
    <CompareButtonControl hideText={hideText} isHeader={isHeader} isAdded={isAdded} onClick={handleClick} />
  );

  if (!withOnboarding || !open) {
    return button;
  }

  return (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      {button}
    </CompareButtonOnboarding>
  );
};
